import React, { useRef, useState, useEffect } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import Footer from '../components/Footer'
import { 
  QrCode, 
  Mic, 
  Settings, 
  CreditCard, 
  Users, 
  Zap, 
  Clock, 
  Shield,
  ArrowRight,
  CheckCircle,
  Sparkles,
  MessageCircle,
  BarChart3,
  Phone,
  Play
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

const Features = () => {
  const { t } = useTranslation()
  const containerRef = useRef(null)
  const isInView = useInView(containerRef, { once: true, margin: "-100px" })
  const [activeFeature, setActiveFeature] = useState(0)
  const [visibleMessages, setVisibleMessages] = useState(1)
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  })

  const y = useTransform(scrollYProgress, [0, 1], [0, -50])
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1, 0.8])

  const features = [
    {
      icon: QrCode,
      title: t('features.featuresList.qrCode.title'),
      description: t('features.featuresList.qrCode.description'),
      gradient: "from-purple-500 to-blue-500",
      details: [
        t('features.featuresList.qrCode.details.0'),
        t('features.featuresList.qrCode.details.1'),
        t('features.featuresList.qrCode.details.2')
      ]
    },
    {
      icon: Mic,
      title: t('features.featuresList.voice.title'),
      description: t('features.featuresList.voice.description'),
      gradient: "from-blue-500 to-purple-500",
      details: [
        t('features.featuresList.voice.details.0'),
        t('features.featuresList.voice.details.1'),
        t('features.featuresList.voice.details.2')
      ]
    },
    {
      icon: Settings,
      title: t('features.featuresList.workflows.title'),
      description: t('features.featuresList.workflows.description'),
      gradient: "from-purple-600 to-blue-600",
      details: [
        t('features.featuresList.workflows.details.0'),
        t('features.featuresList.workflows.details.1'),
        t('features.featuresList.workflows.details.2')
      ]
    },
    {
      icon: CreditCard,
      title: t('features.featuresList.transactional.title'),
      description: t('features.featuresList.transactional.description'),
      gradient: "from-green-500 to-blue-500",
      details: [
        t('features.featuresList.transactional.details.0'),
        t('features.featuresList.transactional.details.1'),
        t('features.featuresList.transactional.details.2')
      ]
    },
    {
      icon: Users,
      title: t('features.featuresList.analytics.title'),
      description: t('features.featuresList.analytics.description'),
      gradient: "from-orange-500 to-red-500",
      details: [
        t('features.featuresList.analytics.details.0'),
        t('features.featuresList.analytics.details.1'),
        t('features.featuresList.analytics.details.2')
      ]
    },
    {
      icon: Zap,
      title: t('features.featuresList.leadCapture.title'),
      description: t('features.featuresList.leadCapture.description'),
      gradient: "from-yellow-500 to-orange-500",
      details: [
        t('features.featuresList.leadCapture.details.0'),
        t('features.featuresList.leadCapture.details.1'),
        t('features.featuresList.leadCapture.details.2')
      ]
    },
    {
      icon: Clock,
      title: t('features.featuresList.availability.title'),
      description: t('features.featuresList.availability.description'),
      gradient: "from-indigo-500 to-purple-500",
      details: [
        t('features.featuresList.availability.details.0'),
        t('features.featuresList.availability.details.1'),
        t('features.featuresList.availability.details.2')
      ]
    },
    {
      icon: Shield,
      title: t('features.featuresList.security.title'),
      description: t('features.featuresList.security.description'),
      gradient: "from-gray-600 to-blue-600",
      details: [
        t('features.featuresList.security.details.0'),
        t('features.featuresList.security.details.1'),
        t('features.featuresList.security.details.2')
      ]
    }
  ]

  const demoMessages = [
    {
      type: 'user',
      message: t('features.demo.messages.0'),
      timestamp: '2:29 PM'
    },
    {
      type: 'bot',
      message: t('features.demo.messages.1'),
      timestamp: '2:30 PM'
    },
    {
      type: 'user',
      message: t('features.demo.messages.2'),
      timestamp: '2:30 PM'
    },
    {
      type: 'bot',
      message: t('features.demo.messages.3'),
      timestamp: '2:31 PM'
    },
    {
      type: 'user',
      message: t('features.demo.messages.4'),
      timestamp: '2:31 PM'
    },
    {
      type: 'bot',
      message: t('features.demo.messages.5'),
      timestamp: '2:32 PM'
    }
  ]

  const demoFeatures = [
    {
      icon: QrCode,
      title: t('features.demo.features.0.title'),
      description: t('features.demo.features.0.description'),
      gradient: 'from-purple-500 to-blue-500',
      stat: t('features.demo.features.0.stat')
    },
    {
      icon: Mic,
      title: t('features.demo.features.1.title'),
      description: t('features.demo.features.1.description'),
      gradient: 'from-blue-500 to-cyan-500',
      stat: t('features.demo.features.1.stat')
    },
    {
      icon: BarChart3,
      title: t('features.demo.features.2.title'),
      description: t('features.demo.features.2.description'),
      gradient: 'from-green-500 to-emerald-500',
      stat: t('features.demo.features.2.stat')
    }
  ]

  // Auto-cycle through features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [features.length])

  // Auto-advance demo messages
  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleMessages(prev => {
        if (prev >= demoMessages.length) {
          return 1
        }
        return prev + 1
      })
    }, 2000)
    return () => clearInterval(timer)
  }, [demoMessages.length])

  return (
    <div ref={containerRef} className="min-h-screen">
      {/* Hero Section - Dark gradient like HeroSection */}
      <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-20 overflow-hidden flex items-center justify-center">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"
          />
          <motion.div 
            animate={{ 
              scale: [1.2, 1, 1.2],
              opacity: [0.15, 0.25, 0.15]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
            className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-400/30 text-purple-300 text-sm font-medium mb-8"
          >
            <Zap className="w-4 h-4 mr-2" />
            {t('features.hero.badge')}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t('features.hero.title1')}
            <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              {t('features.hero.title2')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-12"
          >
            {t('features.hero.description')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(147, 51, 234, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.getElementById('complete-feature-suite')
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              }}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:shadow-2xl hover:shadow-purple-500/25 transition-all duration-300"
            >
              {t('features.hero.exploreButton')}
            </motion.button>
            <Link to="/inquiry">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border-2 border-purple-500/30 text-white font-semibold rounded-xl hover:border-purple-400 hover:bg-purple-500/10 transition-all duration-300"
              >
                {t('features.hero.demoButton')}
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Grid Section - Light gray like ValueProps */}
      <section id="complete-feature-suite" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              {t('features.suite.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-gray-600 max-w-3xl mx-auto"
            >
              {t('features.suite.description')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ 
                  y: -10,
                  boxShadow: "0 25px 50px rgba(0, 0, 0, 0.1)"
                }}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r ${feature.gradient} text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-purple-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {feature.description}
                </p>
                <div className="space-y-2">
                  {feature.details.map((detail, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-500">
                      <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mr-3" />
                      {detail}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo Section - White like HowItWorks */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              {t('features.action.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-gray-600 max-w-3xl mx-auto"
            >
              {t('features.action.description')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('features.action.demoTitle')}</h3>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {demoMessages.slice(0, visibleMessages).map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.3 }}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs px-4 py-3 rounded-2xl ${
                        message.type === 'user' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-white border border-gray-200 text-gray-800'
                      }`}>
                        <p className="text-sm">{message.message}</p>
                        {message.timestamp && (
                          <p className="text-xs opacity-70 mt-1">{message.timestamp}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {demoFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-r ${feature.gradient} flex items-center justify-center`}>
                      <feature.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                      <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
                      <div className="text-xs text-purple-600 font-medium">
                        {feature.stat}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark gradient like CallToAction */}
      <section className="relative min-h-screen bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 py-20 overflow-hidden flex items-center justify-center">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <motion.div 
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.25, 0.1]
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-gradient-to-r from-emerald-400/20 to-teal-400/20 rounded-full blur-3xl"
          />
        </div>

        <motion.div 
          style={{ y, opacity }}
          className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 1.2,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            className="relative"
          >
            <div className="relative backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-3xl p-12 md:p-16 shadow-2xl">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              >
                {t('features.cta.title1')}
                <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  {t('features.cta.title2')}
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-12"
              >
                {t('features.cta.description')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              >
                <Link to="/inquiry">
                  <motion.button
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 25px 50px rgba(16, 185, 129, 0.3)"
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="group relative px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/25"
                  >
                    <span className="relative z-10 flex items-center">
                      {t('features.cta.trialButton')}
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </motion.button>
                </Link>

                <Link to="/inquiry">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 border-2 border-emerald-400/30 text-emerald-300 font-semibold rounded-2xl hover:border-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 flex items-center"
                  >
                    <MessageCircle className="mr-2 w-5 h-5" />
                    {t('features.cta.scheduleButton')}
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      <Footer />
    </div>
  )
}

export default Features